import { omitUndefined } from '@shared/helpers/object.helpers';
import { guards } from '@shared/helpers/typeguards.helpers';
import { Building } from '@shared/models/design/building-design.model';
import { FactoryStorey, Storey } from '@shared/models/design/storey.model';
import { v4 } from 'uuid';
import { createStorey, createStoreys } from './storey.factory';

/**
 * Factory input for a {@link Building}. Omit `storeys` for one default storey;
 * pass `[]` to keep the building empty.
 */
export type FactoryBuilding = Partial<Omit<Building, 'storeys'>> & {
  storeys?: Array<FactoryStorey | Storey>;
};

/**
 * True when {@link storey} is already a full {@link Storey} and must not be
 * re-run through {@link createStorey} (that factory strips existing shape ids).
 */
const isCompleteStorey = (storey: FactoryStorey | Storey): storey is Storey =>
  guards.is.string(storey.id) &&
  guards.is.number(storey.height) &&
  guards.is.array(storey.shapes) &&
  guards.is.array(storey.activities);

/**
 * Resolve factory storeys: default one floor, keep `[]`, pass complete storeys
 * through, and fill partial {@link FactoryStorey} values.
 */
const createBuildingStoreys = (
  storeys: Array<FactoryStorey | Storey> | undefined,
  regenerateIds: boolean,
): Storey[] => {
  if (storeys === undefined) return createStoreys(undefined, regenerateIds);
  if (regenerateIds) {
    return storeys.map((storey) => createStorey(storey, true));
  }
  return storeys.map((storey) =>
    isCompleteStorey(storey) ? storey : createStorey(storey, false),
  );
};

/**
 * Create a {@link Building} with default storeys when none are given.
 * Provided ids are kept (`regenerateIds` defaults to false) so tests can
 * assert on stable building/storey ids without extra wrapping.
 * @param values Overrides for id, name, and storeys
 * @param regenerateIds When true, mint new ids even if values already have them
 */
export const createBuilding = (
  values: FactoryBuilding = {},
  regenerateIds = false,
): Building => {
  const { storeys, id, name } = values;
  return {
    id: regenerateIds || !id ? v4() : id,
    ...omitUndefined({ name }),
    storeys: createBuildingStoreys(storeys, regenerateIds),
  };
};

/**
 * Create one {@link Building} per entry. An empty list yields a single default
 * building so tests never have to special-case `buildings: []` scaffolding.
 * @param values Factory input per building
 * @param regenerateIds Forwarded to {@link createBuilding}
 */
export const createBuildings = (
  values: FactoryBuilding[] = [{}],
  regenerateIds = false,
): Building[] => {
  if (values.length === 0) return [createBuilding({}, regenerateIds)];
  return values.map((value) => createBuilding(value, regenerateIds));
};
